import React, { createContext, useContext, useEffect, useState, ReactNode, useRef } from "react";
import { defaultComponents } from "./defaultComponents";

// Types
export type ComponentType = {
  id: string;
  name: string;
  cost: number;
  costFactor: number;
  pointsPerSecond: number;
  owned: number;
};

export type ClickerContextType = {
  score: number;
  components: ComponentType[];
  handleClick: () => void;
  buyComponent: (id: string) => void;
  setScore: React.Dispatch<React.SetStateAction<number>>;
  resetGame: () => void;
  paused: boolean;
  setPaused: React.Dispatch<React.SetStateAction<boolean>>;
};

const ClickerContext = createContext<ClickerContextType | undefined>(undefined);

function useLocalStorage<T>(key: string, initialValue: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : initialValue;
  });
  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);
  return [value, setValue];
}

function useInterval(callback: () => void, delay: number | null) {
  useEffect(() => {
    if (delay === null) return;
    const id = setInterval(callback, delay);
    return () => clearInterval(id);
  }, [callback, delay]);
}

export const ClickerProvider = ({ children }: { children: ReactNode }) => {
  const [score, setScore] = useLocalStorage<number>("score", 0);
  const [components, setComponents] = useLocalStorage<ComponentType[]>("components", defaultComponents);
  const [paused, setPaused] = useState(false);

  // refs to always have latest value
  const componentsRef = useRef(components);
  const scoreRef = useRef(score);
  useEffect(() => { componentsRef.current = components; }, [components]);
  useEffect(() => { scoreRef.current = score; }, [score]);

  const handleClick = () => setScore(s => s + 1);

  const buyComponent = (id: string) => {
    setComponents(prev => {
      const comp = prev.find(c => c.id === id);
      if (!comp) return prev;
      const owned = typeof comp.owned === 'number' && !isNaN(comp.owned) ? comp.owned : 0;
      const cost = typeof comp.cost === 'number' && !isNaN(comp.cost) ? comp.cost : 1;
      const costFactor = typeof comp.costFactor === 'number' && !isNaN(comp.costFactor) ? comp.costFactor : 1.15;
      const currentCost = Math.ceil(cost * Math.pow(costFactor, owned));
      if (scoreRef.current < currentCost) return prev;
      setScore(s => s - currentCost);
      return prev.map(c =>
        c.id === id ? { ...c, owned: owned + 1 } : c
      );
    });
  };

  // Reset all owned components and score
  const resetGame = () => {
    setComponents(prev => prev.map(c => ({ ...c, owned: 0 })));
    setScore(0);
  };

  // Add points over time with a faster interval and correct time delta
  const intervalMs = 100; // 0.1 second
  const lastTickRef = useRef(Date.now());
  useInterval(() => {
    if (paused) {
      lastTickRef.current = Date.now(); // Reset tick so no time accumulates while paused
      return;
    }
    const now = Date.now();
    const deltaSec = (now - lastTickRef.current) / 1000;
    lastTickRef.current = now;
    const comps = componentsRef.current;
    const totalPPS = comps.reduce((sum, c) => sum + c.pointsPerSecond * c.owned, 0);
    setScore(s => s + totalPPS * deltaSec);
  }, intervalMs);

  return (
    <ClickerContext.Provider value={{ score, components, handleClick, buyComponent, setScore, resetGame, paused, setPaused }}>
      {children}
    </ClickerContext.Provider>
  );
};

export function useClicker() {
  const ctx = useContext(ClickerContext);
  if (!ctx) throw new Error("useClicker must be used within ClickerProvider");
  return ctx;
}
